import type { Partner } from "@expert/shared-types";

/** Valid message sender types in our system. */
export const MESSAGE_SENDER_TYPES = ["customer", "expert", "bot", "system", "internal_note"] as const;

export type MessageSender = (typeof MESSAGE_SENDER_TYPES)[number];
/**
 * For agent messages: whether the customer has read it. (used for ✓✓ display)
 * For customer messages: whether the agent has read it (used for unread count tracking).
 */
export type ReceiptStatus = "unknown" | "delivered" | "read";
export type MessageType = "text" | "media" | "salesDisclosure";

export interface SalesDisclosureAttributes {
    enrollmentUrl: string;
    amountPerMonth: string;
    programDisplayName: string;
    programDescription: string;
    partnerDisplayName: string;
    programSku: string;
    partner: Partner;
    callToAction?: string;
}

/**
 * ChatMessage represents a single message payload in a chat.
 */
export interface ChatMessage {
    id: string;
    index: number;
    senderId: string;
    /** Display name for the sender. Optional since some providers (Twilio) don't provide display names. */
    senderName?: string;
    body: string | null;
    senderType: MessageSender;
    messageType: MessageType;
    dateCreated: Date | null;

    /**
     * If undefined, show to everyone
     */
    showTo?: "customer" | "expert";

    /**
     * For agent messages: whether the customer has read it. (used for ✓✓ display)
     * For customer messages: whether the agent has read it (used for unread count tracking).
     */
    receiptStatus: ReceiptStatus;

    /**
     * Trustworthy server-side time at which our backend received the inbound customer
     * message (`metadata.serverReceivedTimestamp` on Amazon Connect's `ChatEventData.Content`).
     * ISO 8601. Present only on live customer messages relayed through the backend; used as
     * the start of the customer→expert latency metric. Preferred over the customer's
     * client-side clock, which can be skewed or manipulated.
     */
    serverReceivedTimestamp?: string;

    attributes?: Record<string, unknown>; //TODO: enhance this with specific types for known attributes
}

/**
 * State tracking for expert neglection (expert hasn't responded to customer).
 *
 * Lifecycle
 * - State is set when neglection starts (startTime, durationSeconds)
 * - State is cleared entirely (via clearChatNeglection) when neglection ends
 * - Presence of startTime indicates active neglection
 *
 * Note: Backend sends `endTime` in websocket/API responses, but we use it only
 * as a signal to clear state - we don't store it. See event listener hooks.
 *
 */
export interface NeglectionState {
    /**
     * Total duration of the neglection timer in seconds.
     */
    durationSeconds?: number;
    /**
     * Current warning count
     */
    warningCount?: number;
    /**
     * Unix timestamp (ms) when neglection started.
     * Used to calculate timer progress
     * When undefined, neglection is not active.
     */
    startTime?: number;
    /**
     * Count of previous neglections ( how many chat experts were unresponsive ) in this chat session (for transferred chats).
     * Used to show "was previously neglected" indicator.
     */
    neglectionCount?: number;
}

/**
 * State tracking for customer autopilot/absence (customer hasn't responded).
 *
 * Lifecycle
 * - State is set when autopilot starts (startTime, durationSeconds)
 * - State is cleared entirely (via clearChatAutopilot) when autopilot ends
 * - Presence of startTime indicates active autopilot
 *
 * Note: Backend sends `endTime` in websocket/API responses, but we use it only
 * as a signal to clear state - we don't store it. See event listener hooks.
 *
 */
export interface AutopilotState {
    /**
     * Total duration of the autopilot timer in seconds.
     * Used to calculate when timer ends: endsAt = startTime + durationSeconds * 1000
     */
    durationSeconds?: number;
    /**
     * Warning count for autopilot (if applicable).
     */
    warningCount?: number;
    /**
     * Unix timestamp (ms) when autopilot/customer absence started.
     * When undefined, autopilot is not active.
     */
    startTime?: number;
}

export interface ChatClosureInfo {
    contactId: string;
    reason?: string;
    closedAt?: Date;
    partner?: string;
    topic?: string;
    sourceApp?: string;
    language?: string;
}

/** Status of spellcheck operation. @see SpellCheckState */
export type SpellCheckStatus = "idle" | "checking" | "success" | "error" | "suggestions";

/** Response format from spellcheck API. @see SpellCheckResponse */
export interface SpellCheckResponse {
    rewritingNeeded: boolean;
    rewrittenText: string;
    highlightSnippets: string[];
}

/** State for spellcheck per chat. */
export interface SpellCheckState {
    status: SpellCheckStatus;
    /** The content that was last checked - used to skip redundant checks */
    lastCheckedContent?: string;
    /** The result from the last successful spellcheck */
    result?: SpellCheckResponse;
    /** Error message if status is 'error' */
    errorMessage?: string;
    /** Original text before corrections - used for cancel flow */
    originalText?: string;
    /** Whether to show the status UI - only true when send is clicked */
    showStatus?: boolean;
}
